import { http, router } from '@inertiajs/react';
import axios, { type AxiosError } from 'axios';

import {
  HTTP_REQUEST_ERROR_EVENT,
  type HttpRequestErrorDetail
} from '@/constants/httpRequestError';

import { parseJson } from './parseJson';

const VALIDATION_STATUS = 422;
const INERTIA_REDIRECT_STATUS = 409;
const INERTIA_REDIRECT_HEADERS = new Set(['x-inertia-location', 'x-inertia-redirect']);

type ResponseHeaders = Record<string, unknown>;

/**
 * Dispatches {@link HTTP_REQUEST_ERROR_EVENT} for failed requests that carry no validation error
 * bag — 403s, 500s, and other non-422 statuses that forms and field errors never surface. Returns
 * whether the event was dispatched.
 */
export function dispatchHttpRequestError(
  status: number | undefined,
  body: unknown,
  headers?: ResponseHeaders
): boolean {
  if (status === undefined || !isReportedHttpRequestError(status, body, headers)) {
    return false;
  }

  emitHttpRequestError(status, body);

  return true;
}

function emitHttpRequestError(status: number, body: unknown): void {
  const parsedBody = typeof body === 'string' ? parseJson(body) : body;

  window.dispatchEvent(
    new CustomEvent<HttpRequestErrorDetail>(HTTP_REQUEST_ERROR_EVENT, {
      detail: { status, message: extractMessage(parsedBody) }
    })
  );
}

/**
 * A router visit's 422 without an `errors` bag, such as an `abort(422)` refusal, has no form field
 * that can show it.
 */
function isRefusalWithoutErrorBag(status: number, body: unknown): boolean {
  if (status !== VALIDATION_STATUS) {
    return false;
  }

  const parsedBody = typeof body === 'string' ? parseJson(body) : body;

  return !(parsedBody !== null && typeof parsedBody === 'object' && 'errors' in parsedBody);
}

/**
 * Whether the app-wide handler reports this response to the user. A caller that shows its own
 * message for a failed request checks this first, so one failure is not reported twice.
 */
export function isReportedHttpRequestError(
  status: number,
  body: unknown,
  headers?: ResponseHeaders
): boolean {
  if (status < 400 || status === VALIDATION_STATUS) {
    return false;
  }

  if (isInertiaRedirectResponse(status, headers)) {
    return false;
  }

  const parsedBody = typeof body === 'string' ? parseJson(body) : body;

  return !(
    status === INERTIA_REDIRECT_STATUS &&
    parsedBody !== null &&
    typeof parsedBody === 'object' &&
    (parsedBody as { code?: unknown }).code === 'PAGE_DRAFT_CONFLICT'
  );
}

/** {@link isReportedHttpRequestError} for an axios rejection. A request with no response is never reported. */
export function isReportedAxiosError(error: unknown): boolean {
  if (!axios.isAxiosError(error) || typeof error.response?.status !== 'number') {
    return false;
  }

  return isReportedHttpRequestError(
    error.response.status,
    error.response.data,
    error.response.headers as ResponseHeaders
  );
}

/** Rejection handler for an axios response interceptor. */
export function handleHttpRequestAxiosError(error: AxiosError): void {
  dispatchHttpRequestError(error.response?.status, error.response?.data, error.response?.headers);
}

/**
 * Inertia's `useHttp` uses its own HTTP client rather than axios, and its failures are not router
 * visits, so neither the axios interceptor nor `page.props.errors` ever sees them. `http.onError`
 * is the one hook that fires for every Inertia HTTP client request.
 */
export function registerHttpRequestErrorHandler(): () => void {
  return http.onError((error) => {
    const response = (
      error as { response?: { status: number; data: unknown; headers: ResponseHeaders } }
    ).response;

    if (response === undefined) {
      return;
    }

    dispatchHttpRequestError(response.status, response.data, response.headers);
  });
}

/**
 * Router visits (`useForm`, `router.patch`, …) that fail with a non-Inertia response — like a JSON
 * 403 or 422 from `abort()` — fire the global `httpException` event and would otherwise render the
 * raw error response in Inertia's dialog. Dispatch our event instead and suppress that default for
 * the statuses we handle.
 */
export function registerHttpRequestErrorRouterHandler(): VoidFunction {
  return router.on('httpException', (event) => {
    const { status, data, headers } = event.detail.response;

    if (isRefusalWithoutErrorBag(status, data)) {
      emitHttpRequestError(status, data);
      event.preventDefault();

      return;
    }

    if (dispatchHttpRequestError(status, data, headers)) {
      event.preventDefault();
    }
  });
}

/**
 * Inertia signals both external (`Inertia::location()`) and internal redirects with a 409 carrying
 * an `x-inertia-location` / `x-inertia-redirect` header. Its HTTP client rejects on any status
 * >= 400, so those responses reach our error hooks before the router performs the redirect — they
 * are normal responses, not failures. A 409 without either header is a real error and still
 * surfaces.
 */
function isInertiaRedirectResponse(status: number, headers: ResponseHeaders | undefined): boolean {
  if (status !== INERTIA_REDIRECT_STATUS || headers === undefined) {
    return false;
  }

  return Object.keys(headers).some((header) => INERTIA_REDIRECT_HEADERS.has(header.toLowerCase()));
}

function extractMessage(body: unknown): string | null {
  const parsed = typeof body === 'string' ? parseJson(body) : body;

  if (parsed === null || typeof parsed !== 'object') {
    return null;
  }

  const { message, detail } = parsed as { message?: unknown; detail?: unknown };

  if (typeof message === 'string' && message !== '') {
    return message;
  }

  // Problem-details bodies, such as a package refusing a write, carry the reason in `detail`.
  return typeof detail === 'string' && detail !== '' ? detail : null;
}
